import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format, addDays } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Sparkles,
  Wand2,
  AlertTriangle,
  Trash2,
  Check,
  Plus,
  Pencil,
  X,
  Info,
  Loader2,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { formatDate } from '@/utils/dateFormat';

const API = process.env.REACT_APP_BACKEND_URL;

const RULE_LABELS = {
  cycle: 'Continue existing rotation cycle',
  early_start_count: 'Early starters required',
  default_start_time: 'Default start time',
  max_at_time: 'Cap on employees at a start time',
  avoid_consecutive_early: 'Avoid consecutive early starts',
  role_restriction: 'Role restriction',
  rest_hours: 'Minimum rest hours between shifts',
  respect_leave: 'Respect approved leave',
  preserve_pattern: 'Preserve each person\'s pattern',
};

const describeRuleParams = (type, params) => {
  switch (type) {
    case 'early_start_count':
      return `${params?.count ?? '?'} employees at ${params?.time ?? '?'}`;
    case 'default_start_time':
      return `${params?.time ?? '?'}`;
    case 'max_at_time':
      return `≤ ${params?.max ?? '?'} at ${params?.time ?? '?'}`;
    case 'role_restriction':
      return `${params?.user_name ?? 'Someone'} → ${(params?.permitted_start_times || []).join(', ') || 'none'}${params?.only_when_needed ? ' (only when needed)' : ''}`;
    case 'rest_hours':
      return `${params?.min ?? '?'} h`;
    case 'cycle':
      return params?.on_days ? `${params.on_days} on / ${params.off_days} off` : 'saved patterns';
    default:
      return '';
  }
};

const AIRosterAssistant = ({ weekStart, currentWeekStart, onApplied }) => {
  const [rules, setRules] = useState({ requirements: [], preferences: [] });
  const [isLoadingRules, setIsLoadingRules] = useState(false);
  const [chatText, setChatText] = useState('');
  const [parseLoading, setParseLoading] = useState(false);
  const [parsePreview, setParsePreview] = useState(null); // {added, updated, removed_ids, explanation}
  const [generating, setGenerating] = useState(false);
  const [draft, setDraft] = useState(null);
  const [applying, setApplying] = useState(false);
  const [periodDays, setPeriodDays] = useState(14); // next fortnight default
  const [overrideExisting, setOverrideExisting] = useState(false);
  const [showRulesPanel, setShowRulesPanel] = useState(true);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const fetchRules = useCallback(async () => {
    setIsLoadingRules(true);
    try {
      const res = await axios.get(`${API}/api/roster/ai/rules`, { headers });
      setRules(res.data || { requirements: [], preferences: [] });
    } catch (err) {
      toast.error('Failed to load saved rules');
    } finally {
      setIsLoadingRules(false);
    }
  }, []);

  const fetchPendingDraft = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/roster/ai/drafts`, { headers });
      if (res.data?.length) setDraft(res.data[0]);
    } catch (err) {
      // silent
    }
  }, []);

  useEffect(() => {
    fetchRules();
    fetchPendingDraft();
  }, [fetchRules, fetchPendingDraft]);

  const persistRules = async (nextRules) => {
    try {
      await axios.put(`${API}/api/roster/ai/rules`, nextRules, { headers });
      setRules(nextRules);
      toast.success('Rules saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save rules');
    }
  };

  const handleParse = async () => {
    if (!chatText.trim()) return;
    setParseLoading(true);
    setParsePreview(null);
    try {
      const res = await axios.post(
        `${API}/api/roster/ai/parse-rules`,
        { text: chatText, current_rules: rules },
        { headers },
      );
      setParsePreview(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI parse failed');
    } finally {
      setParseLoading(false);
    }
  };

  const applyParsePreview = async () => {
    if (!parsePreview) return;
    const nextReq = [...rules.requirements];
    const nextPref = [...rules.preferences];

    const upsertInto = (list, rule) => {
      const idx = list.findIndex((r) => r.id === rule.id);
      if (idx >= 0) list[idx] = rule;
      else list.push(rule);
    };

    // removed
    const removedIds = new Set(parsePreview.removed_ids || []);
    for (let i = nextReq.length - 1; i >= 0; i--) if (removedIds.has(nextReq[i].id)) nextReq.splice(i, 1);
    for (let i = nextPref.length - 1; i >= 0; i--) if (removedIds.has(nextPref[i].id)) nextPref.splice(i, 1);

    // added + updated
    for (const r of [...(parsePreview.added || []), ...(parsePreview.updated || [])]) {
      if (r.kind === 'requirement') upsertInto(nextReq, r);
      else upsertInto(nextPref, r);
    }

    await persistRules({ requirements: nextReq, preferences: nextPref });
    setParsePreview(null);
    setChatText('');
  };

  const removeRule = async (ruleId, kind) => {
    const next = {
      requirements: kind === 'requirement' ? rules.requirements.filter((r) => r.id !== ruleId) : rules.requirements,
      preferences: kind === 'preference' ? rules.preferences.filter((r) => r.id !== ruleId) : rules.preferences,
    };
    await persistRules(next);
  };

  const toggleRuleKind = async (rule) => {
    const nextKind = rule.kind === 'requirement' ? 'preference' : 'requirement';
    const updated = { ...rule, kind: nextKind };
    const removedFrom = {
      requirements: rules.requirements.filter((r) => r.id !== rule.id),
      preferences: rules.preferences.filter((r) => r.id !== rule.id),
    };
    const next = {
      requirements: nextKind === 'requirement' ? [...removedFrom.requirements, updated] : removedFrom.requirements,
      preferences: nextKind === 'preference' ? [...removedFrom.preferences, updated] : removedFrom.preferences,
    };
    await persistRules(next);
  };

  const generateDraft = async () => {
    if (!weekStart) {
      toast.error('Select a week in the roster first');
      return;
    }
    setGenerating(true);
    try {
      const start = format(weekStart, 'yyyy-MM-dd');
      const end = format(addDays(weekStart, periodDays - 1), 'yyyy-MM-dd');
      const res = await axios.post(
        `${API}/api/roster/ai/generate-draft`,
        { start_date: start, end_date: end, override_existing: overrideExisting },
        { headers },
      );
      setDraft(res.data);
      toast.success(`Draft generated — ${res.data.entries?.length || 0} entries, ${res.data.conflicts?.length || 0} conflicts`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate draft');
    } finally {
      setGenerating(false);
    }
  };

  const applyDraft = async () => {
    if (!draft) return;
    setApplying(true);
    try {
      const res = await axios.post(
        `${API}/api/roster/ai/drafts/${draft.id}/apply`,
        {},
        { headers },
      );
      toast.success(`Applied: ${res.data.created} created, ${res.data.updated} updated`);
      setDraft(null);
      if (onApplied) onApplied();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply draft');
    } finally {
      setApplying(false);
    }
  };

  const discardDraft = async () => {
    if (!draft) return;
    try {
      await axios.delete(`${API}/api/roster/ai/drafts/${draft.id}`, { headers });
      setDraft(null);
      toast.success('Draft discarded');
    } catch (err) {
      toast.error('Failed to discard draft');
    }
  };

  return (
    <Card className="w-full border-indigo-200 shadow-md" data-testid="ai-roster-assistant">
      <CardHeader className="pb-3 bg-gradient-to-r from-indigo-50 to-sky-50 rounded-t-lg">
        <CardTitle className="flex items-center gap-2 text-base">
          <Sparkles className="w-4 h-4 text-indigo-600" />
          AI Rostering Assistant
          <Badge variant="outline" className="ml-auto text-[10px] font-normal">
            Gemini 3 Flash
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 space-y-4">
        {/* Chat / NL rules */}
        <div className="space-y-2">
          <label className="text-xs font-semibold text-slate-700">
            What would you like to change?
          </label>
          <Textarea
            value={chatText}
            onChange={(e) => setChatText(e.target.value)}
            placeholder="e.g. We need two early starters at 4:30am each day. Felipe is early-start relief only."
            rows={3}
            className="text-sm"
            data-testid="ai-roster-chat-input"
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={handleParse}
              disabled={parseLoading || !chatText.trim()}
              data-testid="ai-roster-parse-btn"
            >
              {parseLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Wand2 className="w-3 h-3 mr-1" />}
              Interpret
            </Button>
            {parsePreview && (
              <>
                <Button
                  size="sm"
                  onClick={applyParsePreview}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="ai-roster-apply-parse-btn"
                >
                  <Check className="w-3 h-3 mr-1" /> Save rules
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setParsePreview(null)}>
                  <X className="w-3 h-3" />
                </Button>
              </>
            )}
          </div>
          {parsePreview && (
            <div className="rounded-md border border-indigo-200 bg-indigo-50/50 p-3 text-xs space-y-2" data-testid="ai-roster-parse-preview">
              <div className="font-semibold text-indigo-800">
                Proposed changes
                <span className="ml-2 font-normal text-slate-600">{parsePreview.explanation}</span>
              </div>
              {(parsePreview.added || []).map((r) => (
                <div key={r.id} className="flex items-center gap-2">
                  <Plus className="w-3 h-3 text-green-600" />
                  <Badge variant="outline" className="text-[10px]">{r.kind}</Badge>
                  <span className="font-medium">{RULE_LABELS[r.type] || r.type}</span>
                  <span className="text-slate-500">{describeRuleParams(r.type, r.params)}</span>
                </div>
              ))}
              {(parsePreview.updated || []).map((r) => (
                <div key={r.id} className="flex items-center gap-2">
                  <Pencil className="w-3 h-3 text-amber-600" />
                  <Badge variant="outline" className="text-[10px]">{r.kind}</Badge>
                  <span className="font-medium">{RULE_LABELS[r.type] || r.type}</span>
                  <span className="text-slate-500">{describeRuleParams(r.type, r.params)}</span>
                </div>
              ))}
              {(parsePreview.removed_ids || []).map((id) => (
                <div key={id} className="flex items-center gap-2">
                  <Trash2 className="w-3 h-3 text-red-600" />
                  <span className="text-slate-500">Remove rule {id}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Saved rules */}
        <div className="border-t pt-3">
          <button
            onClick={() => setShowRulesPanel((v) => !v)}
            className="flex items-center gap-2 text-xs font-semibold text-slate-700 hover:text-slate-900 w-full"
            data-testid="ai-roster-toggle-rules"
          >
            {showRulesPanel ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            Saved rules ({(rules.requirements || []).length + (rules.preferences || []).length})
          </button>
          {showRulesPanel && (
            <div className="mt-2 space-y-3">
              <RuleList
                title="Requirements"
                items={rules.requirements || []}
                tone="red"
                onRemove={(r) => removeRule(r.id, 'requirement')}
                onToggleKind={toggleRuleKind}
                describe={describeRuleParams}
                isLoading={isLoadingRules}
              />
              <RuleList
                title="Preferences"
                items={rules.preferences || []}
                tone="indigo"
                onRemove={(r) => removeRule(r.id, 'preference')}
                onToggleKind={toggleRuleKind}
                describe={describeRuleParams}
                isLoading={isLoadingRules}
              />
            </div>
          )}
        </div>

        {/* Generate draft */}
        <div className="border-t pt-3 space-y-2">
          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold text-slate-700 w-28">Period (days)</label>
            <Input
              type="number"
              min={1}
              max={60}
              value={periodDays}
              onChange={(e) => setPeriodDays(parseInt(e.target.value || '14', 10))}
              className="h-8 w-20 text-sm"
              data-testid="ai-roster-period-input"
            />
            <label className="flex items-center gap-2 ml-auto text-xs text-slate-600">
              <input
                type="checkbox"
                checked={overrideExisting}
                onChange={(e) => setOverrideExisting(e.target.checked)}
                data-testid="ai-roster-override-checkbox"
              />
              Override existing shifts
            </label>
          </div>
          <Button
            onClick={generateDraft}
            disabled={generating}
            className="w-full bg-gradient-to-r from-indigo-600 to-sky-600 hover:from-indigo-700 hover:to-sky-700"
            data-testid="ai-roster-generate-btn"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Wand2 className="w-4 h-4 mr-2" />}
            Generate next roster
          </Button>
        </div>

        {/* Draft summary */}
        {draft && (
          <div className="border-t pt-3 space-y-2" data-testid="ai-roster-draft-summary">
            <div className="flex items-center justify-between">
              <div className="text-xs font-semibold text-slate-700">
                Draft for {formatDate(draft.period?.start)} – {formatDate(draft.period?.end)}
              </div>
              <Badge className="bg-slate-100 text-slate-700 text-[10px]">
                {draft.entries?.length || 0} entries
              </Badge>
            </div>
            {draft.conflicts?.length > 0 && (
              <div className="space-y-1">
                {draft.conflicts.map((c, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-2 text-xs"
                    data-testid="ai-roster-conflict"
                  >
                    <AlertTriangle className="w-3 h-3 text-red-600 mt-0.5 shrink-0" />
                    <div>
                      <div className="font-semibold text-red-800">{formatDate(c.date)}</div>
                      <div className="text-red-700">{c.reason}</div>
                      {c.suggest_relax?.length > 0 && (
                        <div className="mt-1 text-[11px] text-red-600">
                          Suggested rules to relax: {c.suggest_relax.join(', ')}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <details className="text-xs">
              <summary className="cursor-pointer text-slate-600 hover:text-slate-900">
                Rationale ({draft.entries?.filter((e) => e.rationale).length || 0} explanations)
              </summary>
              <div className="mt-2 max-h-48 overflow-y-auto space-y-1 pl-2">
                {(draft.entries || []).filter((e) => e.rationale).map((e, idx) => (
                  <div key={idx} className="flex gap-2 text-slate-600">
                    <Info className="w-3 h-3 shrink-0 mt-0.5 text-indigo-500" />
                    <span>
                      <b>{e.user_name || e.user_id}</b> · {formatDate(e.date)} · {e.entry_type === 'shift' ? (e.start_time || '-') : e.entry_type.replace('_', ' ')}
                      <span className="block text-slate-500 italic">{e.rationale}</span>
                    </span>
                  </div>
                ))}
              </div>
            </details>
            <div className="flex gap-2">
              <Button
                onClick={applyDraft}
                disabled={applying}
                size="sm"
                className="flex-1 bg-green-600 hover:bg-green-700"
                data-testid="ai-roster-apply-draft-btn"
              >
                {applying ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Check className="w-3 h-3 mr-1" />}
                Apply changes
              </Button>
              <Button
                onClick={discardDraft}
                size="sm"
                variant="outline"
                className="text-red-600 border-red-200 hover:bg-red-50"
                data-testid="ai-roster-discard-draft-btn"
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const RuleList = ({ title, items, tone, onRemove, onToggleKind, describe, isLoading }) => {
  if (isLoading) {
    return (
      <div className="text-xs text-slate-400 flex items-center gap-2">
        <Loader2 className="w-3 h-3 animate-spin" /> Loading…
      </div>
    );
  }
  if (!items.length) {
    return (
      <div>
        <div className={`text-[11px] font-semibold uppercase tracking-wide text-${tone}-700 mb-1`}>{title}</div>
        <div className="text-xs text-slate-400 italic">None yet.</div>
      </div>
    );
  }
  return (
    <div>
      <div className={`text-[11px] font-semibold uppercase tracking-wide text-${tone}-700 mb-1`}>{title}</div>
      <div className="space-y-1">
        {items.map((r) => (
          <div
            key={r.id}
            className={`flex items-center gap-2 rounded-md border border-${tone}-100 bg-${tone}-50/40 px-2 py-1 text-xs`}
            data-testid={`ai-roster-rule-${r.id}`}
          >
            <span className="font-medium">{RULE_LABELS[r.type] || r.type}</span>
            <span className="text-slate-600 truncate">{describe(r.type, r.params)}</span>
            <button
              onClick={() => onToggleKind(r)}
              className="ml-auto text-[10px] text-slate-500 hover:text-slate-800"
              title={`Make this a ${r.kind === 'requirement' ? 'preference' : 'requirement'}`}
            >
              → {r.kind === 'requirement' ? 'preference' : 'requirement'}
            </button>
            <button
              onClick={() => onRemove(r)}
              className="text-slate-400 hover:text-red-600"
              aria-label="Remove rule"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AIRosterAssistant;
